import type { NavigationInput } from "../lib/navigation.types.js";
import type {
  NavigationParameters,
  ParameterRecord,
  ParametersDefinition,
  ParamType,
} from "./params.types.js";
import type { ObjectToEntries, ObjectToEntry } from "../lib/utils.js";
import { baseLocation } from "../lib/location.js";
import { locationToParams } from "./params.input.js";
import {
  coerceValue,
  appendEntries,
  getObjectReducer,
  isValidKey,
  mergeParamCache,
  mergeWithCurrent,
  paramTypes,
  toEntries,
  toStringParams,
  typeDelimiterMap,
  uncoerceValue,
  fromPlaceholders,
  toPlaceholders,
} from "./params.utils.js";

/**
 *
 *
 *
 *
 *
 *
 *
 *
 *
 */
export const createParams = <P extends ParameterRecord>(
  location: NavigationInput,
  type: ParamType,
  filter: string[] = [],
  base = baseLocation,
): NavigationParameters<P> => {
  let [url, raw] = locationToParams(location, type, base);

  /**
 *
 *
 *
 */
  const deletedKeys = new Set<string>();
  const markDeleted = (k: string): void => {
    if (filter.length === 0 || filter.includes(k)) deletedKeys.add(k);
  };

  const self: NavigationParameters<P> = {
    /*              */

    get [Symbol.toStringTag]() {
      return `${type[0].toUpperCase()}${type.slice(1)}Parameters`;
    },

    get type() {
      return type;
    },

    get deletedKeys() {
      return new Set(deletedKeys);
    },

    /*        */

    /*               */

    *[Symbol.iterator]() {
      yield* self.entries();
    },

    *entries() {
      for (const [key, value] of raw) {
        if (isValidKey(key, filter)) yield [key, fromPlaceholders(value)] as ObjectToEntry<P>;
      }
    },

    *keys() {
      for (const key of raw.keys()) {
        if (isValidKey(key, filter)) yield key as keyof P;
      }
    },

    *values() {
      for (const [key, value] of raw) {
        if (isValidKey(key, filter)) yield fromPlaceholders(value) as P[keyof P];
      }
    },

    /*        */

    /*             */

    has: (k) => {
      return isValidKey(k as string, filter) && raw.has(k as string);
    },

    get: (k) => {
      if (!self.has(k)) return null;
      return fromPlaceholders(raw.get(k as string)!);
    },

    getAll: (k) => {
      if (!self.has(k)) return [];
      return raw.getAll(k as string).map(fromPlaceholders);
    },

    coerce: (key) => {
      return self.has(key) ? (coerceValue(fromPlaceholders(self.get(key)!)) as never) : null;
    },

    coerceAll: (key) => {
      return self.getAll(key).map((v) => coerceValue(fromPlaceholders(v)) as never);
    },

    /*        */

    /*                   */

    set: (k, v) => {
      if (!isValidKey(k as string, filter)) return;
      raw.set(k as string, toPlaceholders(uncoerceValue(v)));
    },

    append: (k, v) => {
      if (!isValidKey(k as string, filter)) return;
      raw.append(k as string, toPlaceholders(uncoerceValue(v)));
    },

    delete: (k, v) => {
      if (!isValidKey(k as string, filter)) return;

      if (v === undefined) {
        raw.delete(k as string);
      } else {
        raw.delete(k as string, toPlaceholders(uncoerceValue(v)));
      }

      /*                                                                                   */
      /*                                       */
      markDeleted(k as string);
    },

    /*        */

    /*                    */

    clear: () => {
      /*                                                    */
      for (const key of [...raw.keys()]) {
        if (!isValidKey(key, filter)) continue;
        raw.delete(key);
        markDeleted(key);
      }

      return self;
    },

    appendMany: (...entries) => {
      for (const [key, value] of entries) {
        if (!isValidKey(key as string, filter)) continue;
        raw.append(key as string, toPlaceholders(uncoerceValue(value)));
      }
      return self;
    },

    fromEntries: (entries, options = {}) => {
      if (!options.patch) self.clear();
      appendEntries(raw, entries);
      return self;
    },

    fromObject: (entries, options = {}) => {
      if (!options.patch) self.clear();
      appendEntries(raw, Object.entries(entries) as ObjectToEntries<unknown>);
      return self;
    },

    fromString: (params, options = {}) => {
      if (!options.patch) self.clear();

      for (const [key, value] of locationToParams(params, type, base)[1]) {
        raw.append(key, toPlaceholders(value));
      }

      return self;
    },

    /*        */

    /*                 */

    toRecord: (current) => {
      return self.toEntries(current).reduce(getObjectReducer(), {}) as never;
    },

    toObject: (current) => {
      return self.toEntries(current).reduce(getObjectReducer(coerceValue), {}) as never;
    },

    toEntries: (current) => {
      if (!current) return [...self] as never;
      const [, curParams] = mergeWithCurrent(
        raw,
        filter,
        deletedKeys,
        ...locationToParams(current, type, base),
      );
      return toEntries(curParams);
    },

    toString: (current) => {
      const params = current
        ? mergeParamCache(locationToParams(current, type, base)[1], raw, filter, deletedKeys)
        : mergeParamCache(new URLSearchParams(), raw, filter, deletedKeys);

      const result = toStringParams(params, base);
      return result ? typeDelimiterMap[type] + result : "";
    },

    toUrl: (current) => {
      if (!current) {
        const resUrl = new URL(url);
        resUrl[type] = self.toString();
        return resUrl;
      }

      const [resUrl, curParams] = mergeWithCurrent(
        raw,
        filter,
        deletedKeys,
        ...locationToParams(current, type, base),
      );
      resUrl[type] = toStringParams(curParams, base);
      return resUrl;
    },

    /*        */

    /*                     */

    parse: (schema, params) => {
      return schema.parse(self.toRecord(), params);
    },

    parseSafe: (schema, params) => {
      return schema.safeParse(self.toRecord(), params);
    },

    /*        */

    /*                     */

    clone: () => {
      return createParams(toStringParams(raw, base), type, filter);
    },

    reset: (current) => {
      [url, raw] = locationToParams(current ?? url, type, base);
      deletedKeys.clear();
      return self;
    },

    /*        */
  };

  return self;
};

/**
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 */
export const searchParams = <P extends ParameterRecord>(
  location: NavigationInput,
  filter?: string[],
): NavigationParameters<P> => {
  return createParams(location, paramTypes[0], filter);
};

/**
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 */
export const hashParams = <P extends ParameterRecord>(
  location: NavigationInput,
  filter?: string[],
): NavigationParameters<P> => {
  return createParams(location, paramTypes[1], filter);
};

/**
 *
 *
 *
 *
 *
 *
 */
export const paramsMap = <P extends ParameterRecord>(
  keys: (keyof P)[],
): ParametersDefinition<P, true> => keys as never;
